class CartSummary {
    constructor(config) {
        const urlParams = new URLSearchParams(window.location.search);
        const debugFromURL = urlParams.get("gar_debug") === "true";

        if (!config) config = {};
        this.config = Object.assign(
            {
                DEBUG: debugFromURL,
                LOCALE: "en-GB",
                LANGUAGE: "en",
                DISCOUNT_VALUE: 0,
                MINIMUM_CART_VALUE: 0,
                CONTAINER_SELECTOR: "#cart-items",
                SUBTOTAL_SELECTOR: "#subtotal",
                DISCOUNT_SELECTOR: "#discount",
                TOTAL_SELECTOR: "#total-price",
                HALF_TOTAL_SELECTOR: ".total-price", // multiple matching elements
            },
            config
        );

        this.log = (...args) =>
            this.config.DEBUG && console.log("🟢 pro-forma-cart-summary:", ...args);
        this.beginCheckoutPublishedForView = false;

        this.translations = {
            en: {
                cartEmpty: "Your cart is empty.",
                cartLoadError: "Could not load cart.",
            },
            de: {
                cartEmpty: "Ihr Warenkorb ist leer.",
                cartLoadError: "Warenkorb konnte nicht geladen werden.",
            },
            fr: {
                cartEmpty: "Votre panier est vide.",
                cartLoadError: "Impossible de charger le panier.",
            },
            pl: {
                cartEmpty: "Twój koszyk jest pusty.",
                cartLoadError: "Nie udało się załadować koszyka.",
            },
            cz: {
                cartEmpty: "Váš košík je prázdný.",
                cartLoadError: "Nepodařilo se načíst košík.",
            },
        };
    }

    t(key) {
        var lang = this.config.LANGUAGE;
        if (this.translations[lang] && this.translations[lang][key])
            return this.translations[lang][key];
        if (this.translations.en[key]) return this.translations.en[key];
        return key;
    }

    markBeginCheckoutPublished(cart) {
        if (!cart || !cart.items || !cart.items.length) return false;
        if (this.beginCheckoutPublishedForView) return false;
        this.beginCheckoutPublishedForView = true;
        return true;
    }

    async fetchCart() {
        var res = await fetch("/cart.js");
        if (!res.ok) throw new Error(this.t("cartLoadError"));
        return await res.json();
    }

    formatCurrency(value, currency) {
        return value.toLocaleString(this.config.LOCALE, {
            style: "currency",
            currency: currency,
        });
    }

    setContainerHTML(html) {
        var container = document.querySelector(this.config.CONTAINER_SELECTOR);
        if (container) container.innerHTML = html;
    }

    async init() {
        try {
            var cart = await this.fetchCart();

            if (cart?.items?.length) {
                if (this.markBeginCheckoutPublished(cart)) {
                    if (window.Shopify?.analytics?.publish) {
                        Shopify.analytics.publish("custom_cart_data", {
                            cart,
                            source: "page_load",
                        });
                        this.log("Published Shopify.analytics event: custom_cart_data", {
                            source: "page_load",
                            token: cart?.token || cart?.id || null,
                        });
                    } else {
                        this.log("⚠️ Shopify.analytics.publish unavailable");
                    }
                } else {
                    this.log("Skipped duplicate custom_cart_data publish", {
                        source: "page_load",
                        token: cart?.token || cart?.id || null,
                    });
                }
            }

            this.renderCart(cart);
        } catch (error) {
            console.error("❌", error);
            this.setContainerHTML("<li>" + this.t("cartLoadError") + "</li>");
        }
    }

    renderCart(cart) {
        var container = document.querySelector(this.config.CONTAINER_SELECTOR);
        if (!container) return;

        if (!cart.items.length) {
            this.setContainerHTML("<li>" + this.t("cartEmpty") + "</li>");
            var subtotalEl = document.querySelector(this.config.SUBTOTAL_SELECTOR);
            if (subtotalEl) subtotalEl.textContent = "";
            return;
        }

        // Base values
        var subtotalValue = cart.items_subtotal_price / 100;
        var subtotalFormatted = this.formatCurrency(subtotalValue, cart.currency);

        var totalValue = cart.total_price / 100;
        var discountApplied = 0;

        if (totalValue >= this.config.MINIMUM_CART_VALUE) {
            discountApplied = this.config.DISCOUNT_VALUE;
            totalValue -= discountApplied;
        }

        // ✅ Fix: omit "-" if no discount
        var discountFormatted =
            discountApplied > 0
                ? this.formatCurrency(-discountApplied, cart.currency)
                : this.formatCurrency(0, cart.currency);

        var totalFormatted = this.formatCurrency(totalValue, cart.currency);

        // Half total (50%)
        var halfTotalValue = (cart.total_price / 100) * 0.5;
        var halfTotalFormatted = this.formatCurrency(halfTotalValue, cart.currency);

        // Render items
        container.innerHTML = "";
        for (var i = 0; i < cart.items.length; i++) {
            var item = cart.items[i];
            var props = item.properties || {};
            var optionsHTML = "";
            for (var key in props) {
                if (props.hasOwnProperty(key) && props[key] && key.indexOf("_") !== 0) {
                    optionsHTML += "<div>" + key + ": " + props[key] + "</div>";
                }
            }

            var li = document.createElement("li");
            var lineLevelDiscount =
                item.line_level_total_discount || item.total_discount || 0;
            var originalLineCents =
                item.original_line_price ??
                item.line_price ??
                item.final_line_price ??
                0;
            // Prefer final_line_price so coupons reflected in UI; compute fallback with discounts
            var discountedLineCents =
                item.final_line_price ??
                (item.line_price != null
                    ? item.line_price - lineLevelDiscount
                    : item.original_line_price != null
                        ? item.original_line_price - lineLevelDiscount
                        : 0);
            var perUnitDiscountedValue =
                (item.final_price != null
                    ? item.final_price
                    : discountedLineCents / Math.max(item.quantity || 1, 1)) / 100;
            var originalLineValue = originalLineCents / 100;
            var discountedLineValue = discountedLineCents / 100;
            var hasLineDiscount = originalLineValue > discountedLineValue;
            var self = this;

            var lineDiscounts = (item.discount_allocations || []).map(function (d) {
                var app = d.discount_application || {};
                var title = d.title || app.code || app.title || "Discount";
                return {
                    title: title,
                    amount: d.amount / 100,
                };
            });
            var couponTitle = lineDiscounts.length ? lineDiscounts[0].title : "";

            var discountsHTML = lineDiscounts
                .map(
                    function (d) {
                        return (
                            '<div class="line-discount">' +
                            d.title +
                            ": " +
                            self.formatCurrency(d.amount, cart.currency) +
                            "</div>"
                        );
                    }
                )
                .join("");

            var priceHTML =
                '<div class="item-price">' +
                (hasLineDiscount
                    ? '<span class="price-before">' +
                    this.formatCurrency(originalLineValue, cart.currency) +
                    "</span>"
                    : "") +
                '<span class="price-after">' +
                this.formatCurrency(discountedLineValue, cart.currency) +
                "</span>" +
                "</div>" +
                (couponTitle
                    ? '<div class="item-coupon">Coupon: ' + couponTitle + "</div>"
                    : "") +
                discountsHTML;

            li.innerHTML =
                '<div class="thumb-container">' +
                '<img src="' +
                item.image +
                '" alt="' +
                item.title +
                '" />' +
                '<span class="qty-badge">' +
                item.quantity +
                "</span>" +
                "</div>" +
                "<div>" +
                '<div class="item-title">' +
                item.title +
                "</div>" +
                '<div class="item-properties">' +
                optionsHTML +
                "</div>" +
                priceHTML +
                "</div>";
            container.appendChild(li);
        }

        // Update totals
        var subtotalNode = document.querySelector(this.config.SUBTOTAL_SELECTOR);
        if (subtotalNode) subtotalNode.textContent = subtotalFormatted;

        var discountNode = document.querySelector(this.config.DISCOUNT_SELECTOR);
        if (discountNode) discountNode.textContent = discountFormatted;

        var totalNode = document.querySelector(this.config.TOTAL_SELECTOR);
        if (totalNode) totalNode.textContent = totalFormatted;

        var halfTotalNodes = document.querySelectorAll(this.config.HALF_TOTAL_SELECTOR);
        for (var j = 0; j < halfTotalNodes.length; j++) {
            halfTotalNodes[j].textContent = halfTotalFormatted;
        }

        (async () => {
            try {
                const res = await fetch("/cart.js");
                if (!res.ok) throw new Error("Cart fetch failed");
                const cart = await res.json();

                if (!this.markBeginCheckoutPublished(cart)) {
                    this.log("Skipped duplicate custom_cart_data publish", {
                        source: "cart_summary_render",
                        token: cart?.token || cart?.id || null,
                    });
                    return;
                }

                if (window.Shopify?.analytics?.publish) {
                    Shopify.analytics.publish("custom_cart_data", {
                        cart,
                        source: "cart_summary_render",
                    });
                    this.log("Published Shopify.analytics event: custom_cart_data", cart);
                } else {
                    this.log("⚠️ Shopify.analytics.publish unavailable");
                }
            } catch (err) {
                this.log("❌ Error publishing custom_cart_data:", err);
            }
        })();


        this.log(
            "Cart summary: subtotal " +
            subtotalFormatted +
            ", discount " +
            discountFormatted +
            ", half " +
            halfTotalFormatted +
            ", total " +
            totalFormatted
        );
    }
}
